'use client';

/**
 * API namespace `admin` — orchestrator + top-level methods.
 * Verbatim extraction from frontend/src/lib/api-client.ts:
 *   - admin top-level methods: L2990-L3624 (before academy sub-namespace)
 *   - admin header: L2987 (moved verbatim)
 *   - admin.academy: imported from ./admin-academy
 *   - admin.market: imported from ./admin-market
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch, apiFetchValidated } from '../core/fetch-client';

import {
  accountingSummaryResponseSchema,
  awaitingSignaturesResponseSchema,
  prepareTxResponseSchema,
  submitTxResponseSchema,
  payoutTxStatusResponseSchema,
} from '../core/shared-types';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

import { academy as adminAcademy } from './admin-academy';
import { market as adminMarket } from './admin-market';

// ─── V126-F3-ADMIN-AUTO-WITHDRAW-DASHBOARD — DTOs for the admin monitoring
// dashboard. Mirrors the backend interfaces in
// `backend/src/features/admin/auto-withdraw.ts` (FE-side types — same field
// names + shapes). Anti-§6 (CM-19/CM-15): `PayoutDto` + `Paginated<T>` are
// reused from `./core/shared-types` (single source of truth — NO duplication
// of the existing payout DTO for the history endpoint).
export interface AutoWithdrawStats {
  /** Count of users with `wallet.autoWithdrawEnabled === true`. */
  totalOptInUsers: number;
  /** Count of Payouts where `autoWithdraw === true` AND `createdAt >= today 00:00 UTC`. */
  todayCount: number;
  /** Sum of `amount` (Decimal string) for the same filter as `todayCount`. */
  todayTotalAmount: string;
  /**
   * Percentage (0..100) of `autoWithdraw=true` Payouts in the last 30 days
   * that reached `status='completed'`. Returns `0` when the denominator is
   * zero (no auto-withdraw payouts in the window).
   */
  successRate: number;
  /** Last 7 calendar days (UTC), oldest first. */
  last7Days: Array<{
    /** ISO date `YYYY-MM-DD` (UTC). */
    date: string;
    count: number;
    totalAmount: string;
  }>;
}

export interface AutoWithdrawUserDto {
  userId: string;
  username: string;
  /** Decrypted AES-256-GCM email (INV-SEC-05 — PII cifrada en reposo). */
  email: string;
  kycLevel: number;
  /** Wallet chain the user configured for withdrawals (BSC | Polygon | Arbitrum). */
  walletChain: 'BSC' | 'Polygon' | 'Arbitrum' | null;
  /** Decimal string (mirrors `wallet.autoWithdrawMinAmount` schema). */
  autoWithdrawMinAmount: string;
  /** ISO timestamp of the user's most recent Payout. Null if none. */
  lastPayoutAt: string | null;
  /** Status of the user's most recent Payout. Null if the user has no Payouts. */
  lastPayoutStatus: PayoutStatus | null;
}

/** Query params for `api.admin.listAutoWithdrawHistory`. */
export interface ListAutoWithdrawHistoryQuery {
  page?: number;
  limit?: number;
  status?: PayoutStatus;
  chain?: 'BSC' | 'Polygon' | 'Arbitrum';
  /** ISO 8601 date string — `createdAt >= dateFrom`. */
  dateFrom?: string;
  /** ISO 8601 date string — `createdAt <= dateTo`. */
  dateTo?: string;
}

export const admin = {
  // ADM-01: Users — D-013 fix (SPEC-043 Batch D): added optional `signal` param.
  // AUDIT-5-013 fix (QA Co-founder SPEC-046): add `limit` (canonical per CONTRACTS §0.3).
  // AUDIT3-SHARED-006 (SPEC-046 QA AUDIT R1 IA — BATCH-6): typed return —
  // `Paginated<AdminUserDto>` (no more `any`).
  listUsers(params?: { page?: number; limit?: number; pageSize?: number; search?: string; role?: string; status?: string; country?: string; state?: string; city?: string }, signal?: AbortSignal): Promise<Paginated<AdminUserDto>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    else if (params?.pageSize) query.set('pageSize', String(params.pageSize));
    if (params?.search) query.set('search', params.search);
    if (params?.role) query.set('role', params.role);
    if (params?.status) query.set('status', params.status);
    if (params?.country) query.set('country', params.country);
    if (params?.state) query.set('state', params.state);
    if (params?.city) query.set('city', params.city);
    return apiFetch<Paginated<AdminUserDto>>(`/admin/users?${query.toString()}`, { signal });
  },
  async getUserDetail(userId: string): Promise<AdminUserDetailDto> {
    return apiFetch<AdminUserDetailDto>(`/admin/users/${userId}`);
  },
  // ADMIN-TRANSPARENCY (Task ID: ADMIN-TRANSPARENCY) — Feature 3: paginated
  // ledger entries for a single user (across all their account strings).
  // The response shape mirrors `getLedgerEntries` (Paginated<LedgerEntry>).
  // Anti-§6 (CM-19/CM-15): no new types invented — reuses the existing
  // `AccountingLedgerEntry` shape (same fields the accounting page renders).
  getUserLedger(
    userId: string,
    params?: { page?: number; limit?: number },
    signal?: AbortSignal,
  ): Promise<{
    items: AccountingLedgerEntry[];
    total: number;
    page: number;
    limit: number;
    pageSize: number;
    totalPages: number;
  }> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    return apiFetch(`/admin/users/${userId}/ledger?${query.toString()}`, { signal });
  },
  // AUDIT3-SHARED-006: typed return — `AdminUserDto` (no more `any`).
  updateUser(userId: string, data: { role?: string; status?: string; forceKyc?: boolean }): Promise<AdminUserDto> {
    return apiFetch<AdminUserDto>(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  // AUDIT-070-002 (SPEC-070 QA Audit staging 2026-08-25 v6 — HIGH):
  // Admin role-override — PUT /admin/users/:userId/role. Backend route
  // (backend/src/routes/admin.route.ts:121) is `requireSuperAdmin` +
  // `requireRecent2FA` (INV-SEC-06 §9.6 — irreversible ops require
  // superadmin + 2FA recent). The backend validates the body via
  // `updateUserRoleTypeSchema` (zod strict — `roleType: 'networker' |
  // 'cliente'` + optional `note: string <= 500 chars`).
  //
  // SPEC-068 / ADR-037 §3.7 (INV-CLI-08): superadmin can force-change a
  // user's roleType between 'networker' and 'cliente', bypassing the R-02
  // downline restriction (R-03 — admin override). The change is audit-
  // logged with `from → to` + the admin's note.
  //
  // The endpoint was implemented in Sprint 17 / SPEC-068 but the admin
  // users UI never grew a button for it — admins had to curl the API
  // directly. This method gives the UI a typed entry point.
  //
  // §6 Anti-Invención: NO new business logic — UI for an existing
  // endpoint. Trazabilidad §6: SPEC-070 §AUDIT-070-002,
  // SPEC-068 §3.7 (ADR-037 admin override).
  updateUserRoleType(
    userId: string,
    data: { roleType: 'networker' | 'cliente'; note?: string },
  ): Promise<AdminUserDto> {
    return apiFetch<AdminUserDto>(`/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  // SPEC-246 Founder directive 2026-10-24: unverify KYC.
  // POST /admin/users/:userId/unverify-kyc. Backend route is
  // `requireAdmin` + `requireRecent2FA` (irreversible — resets user KYC).
  // Returns { ok: true }.
  async unverifyUserKyc(userId: string): Promise<{ ok: true }> {
    return apiFetch<{ ok: true }>(`/admin/users/${userId}/unverify-kyc`, {
      method: 'POST',
    });
  },
  // Sprint 128 F8 (SPEC-239 §3.4 — ADM-01c): per-user SFU flag toggle.
  // PATCH /admin/users/:userId/feature-flags/sfu with body `{ enabled }`.
  // Backend route is `requireSuperAdmin` (no `requireRecent2FA` — toggling
  // a feature flag is NOT a financial/irreversible action per §9.6 SPEC-
  // NEGOCIO). Audit-logged via `admin_user_feature_flag` action.
  //
  // §6 Anti-Invención: this method does NOT invent business rules — it
  // just persists a boolean on the User document. The runtime effect
  // (SFU vs relay) is decided by `useSfuFlag()` (F7) which reads the
  // flag via `GET /api/config/sfu` (F6).
  setUserSfuFlag(
    userId: string,
    enabled: boolean,
  ): Promise<{ userId: string; flag: string; enabled: boolean }> {
    return apiFetch<{ userId: string; flag: string; enabled: boolean }>(
      `/admin/users/${userId}/feature-flags/sfu`,
      {
        method: 'PATCH',
        body: JSON.stringify({ enabled }),
      },
    );
  },
  // PR #996 (IMPL-ADMIN-PURGE-USER-UI-996) — Admin purge user UI.
  // Founder directive (2026-09-29): "el Admin en la UI tenga la opción de
  // eliminar un usuario mal registrado, con un icono de eliminar y
  // confirmando con 2FA y internamente se haga lo que hace este script?"
  //
  // Two endpoints — both delegate ALL business logic to the backend
  // service `admin-purge-user.service.ts:purgeUser()` (anti-§6 — thin
  // assemblers). The service reuses the EXACT same 3-tier purge logic
  // as `scripts/purge-user.ts` (PR #994 — anti-P-347, zero divergence).
  //
  // Auth (anti-T-004, 3-layer defense-in-depth):
  //   GET /admin/users/:userId/purge-preview → requireAuth + requireAdmin
  //     (read-only — NO 2FA required per task spec). Returns what WOULD
  //     be deleted without touching the DB.
  //   DELETE /admin/users/:userId → requireAuth + requireSuperAdmin +
  //     requireRecent2FA (irreversible — only superadmin + 2FA fresh).
  //
  // When the DELETE endpoint returns 403 `code:'2FA_REQUIRED'`, the
  // apiFetch wrapper dispatches the `profileia:admin-2fa-required` window
  // event (lib/api-client.ts:1747). The admin-shell.tsx layout listens
  // for this event and opens the global 2FA re-verify dialog (Bloque E
  // INT-006). After 2FA is verified, the admin retries the DELETE call
  // (the 2FA dialog dispatches `profileia:admin-2fa-verified` which the
  // page can listen for to re-fire the action).
  //
  // §6 Anti-Invención: NO new business logic — UI for an existing service.
  // Trazabilidad §6: PR #994 (script), PR #996 (this UI).
  purgeUserPreview(userId: string): Promise<AdminPurgeUserResultDto> {
    return apiFetch<AdminPurgeUserResultDto>(
      `/admin/users/${userId}/purge-preview`,
      { method: 'GET' },
    );
  },
  purgeUser(userId: string): Promise<AdminPurgeUserResultDto> {
    return apiFetch<AdminPurgeUserResultDto>(
      `/admin/users/${userId}`,
      { method: 'DELETE' },
    );
  },
  // ADM-02: KYC (CONTRACTS-ADMIN-API §4.1 — Paginated<KycApplicationDto>).
  // SPEC-040 D3 — backend returns canonical `limit` (per D3 alignment
  // commit `1ef1549` that refactored all 8 admin list endpoints to use
  // `parsePagination` helper). `pageSize` kept as legacy alias for callers
  // that haven't migrated yet (Sprint 16 removal).
  listKyc(params?: {
    page?: number;
    limit?: number;
    pageSize?: number;  // deprecated alias for `limit`
    status?: 'pending_docs' | 'pending_review' | 'approved' | 'rejected';
    country?: string;
    userId?: string;
  }, signal?: AbortSignal): Promise<Paginated<KycApplicationDto>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    // `limit` is the canonical param (CONTRACTS §0.3). `pageSize` kept
    // as legacy alias for callers that haven't migrated yet.
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    else if (params?.pageSize) query.set('pageSize', String(params.pageSize));
    if (params?.status) query.set('status', params.status);
    if (params?.country) query.set('country', params.country);
    if (params?.userId) query.set('userId', params.userId);
    // D-013 fix (SPEC-043 Batch D): pass signal to apiFetch.
    return apiFetch<Paginated<KycApplicationDto>>(`/admin/kyc?${query.toString()}`, { signal });
  },
  // SPEC-040 D3 — `reviewKyc` kept as PUT (NOT migrated to POST /approve|reject
  // per CONTRACTS §4.2/§4.3) because IA#1 D3 worklog confirms the backend
  // still uses the legacy single PUT /admin/kyc/:applicationId endpoint
  // with `{ decision, rejectionReason }` body. The CONTRACTS §4.2/§4.3
  // POST /approve|reject endpoints are the FUTURE contract — Sprint 16
  // migration. The `ApproveKycApplicationBody`/`RejectKycApplicationBody`
  // DTOs (admin-dtos.ts:193-204) describe the FUTURE body shape; we don't
  // use them yet because the current backend expects `{ decision,
  // rejectionReason }`. Anti-§6: signature matches actual backend impl.
  //
  // TODO(4F-MED-002): Migrate `reviewKyc` to separate `approveKyc`/
  // `rejectKyc` methods using POST endpoints per CONTRACTS-ADMIN-API
  // spec §4.2 (POST /admin/kyc-applications/:id/approve) and §4.3
  // (POST /admin/kyc-applications/:id/reject). The current PUT endpoint
  // is a legacy monolith that will be removed once both POST endpoints
  // are active on the backend.
  reviewKyc(applicationId: string, decision: 'approved' | 'rejected', rejectionReason?: string) {
    return apiFetch(`/admin/kyc/${applicationId}`, {
      method: 'PUT',
      body: JSON.stringify({ decision, rejectionReason }),
    });
  },
  // Bug #32 fix (R3 CORROBORACIÓN QA): PATCH endpoint to update adminNotes
  // WITHOUT changing approval status (no auto-approve on note save).
  // Kept as PATCH per Bug #32 — already correct (CONTRACTS §4.4 PATCH /notes).
  updateKycNotes(applicationId: string, notes: string) {
    return apiFetch(`/admin/kyc/${applicationId}/notes`, {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    });
  },
  // ADM-03: Deposits (CONTRACTS-ADMIN-API §5.0 — Paginated<DepositDto>).
  // SPEC-040 D4 alignment: `pageSize` → `limit` (canonical per §0.3).
  // D-013 fix (SPEC-043 Batch D): added optional `signal` param.
  listDeposits(params?: { page?: number; limit?: number; status?: DepositStatus }, signal?: AbortSignal): Promise<Paginated<DepositDto>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);
    return apiFetch<Paginated<DepositDto>>(`/admin/deposits?${query.toString()}`, { signal });
  },
  resolveDeposit(depositId: string, resolution: 'accept' | 'reject') {
    return apiFetch<{ success: boolean }>(`/admin/deposits/${depositId}`, {
      method: 'PUT',
      body: JSON.stringify({ resolution }),
    });
  },
  // SPEC-038 A42 fix (Wave 3 FE-2): wire the existing
  // POST /admin/deposits/:depositId/resolve-unverified endpoint
  // (BUG-BE-002 fix, Sprint 14) to the admin deposits UI. The endpoint
  // resolves deposits stuck in `unverified_amount` status (no matching
  // purchase intent at detection time → Bug #18 fail-closed). The admin
  // chooses `confirm` (with the expectedAmount the user SHOULD have sent)
  // or `reject` (mark failed, no credit). See
  // backend/src/services/admin.service.ts:resolveUnverifiedDeposit for
  // the full fail-closed guarantees.
  resolveUnverifiedDeposit(
    depositId: string,
    action: 'confirm' | 'reject',
    expectedAmount?: string,
  ) {
    return apiFetch<{ success: boolean }>(`/admin/deposits/${depositId}/resolve-unverified`, {
      method: 'POST',
      body: JSON.stringify({ action, expectedAmount }),
    });
  },
  // ADM-04: Accounting (legacy — kept for backward compat, prefer getAccountingSummary)
  // SPEC-040 §3.3 Track 12 D8 — the legacy `GET /admin/accounting` returns
  // `{ summary, ledger }` (legacy shape without perNetwork). New consumers
  // should use `getAccountingSummary()` + `getLedgerEntries()` separately
  // for the typed DTOs with perNetwork breakdown.
  // D-013 fix (SPEC-043 Batch D): added optional `signal` param.
  getAccounting(params?: { page?: number; pageSize?: number; account?: string }, signal?: AbortSignal) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.pageSize) query.set('pageSize', String(params.pageSize));
    if (params?.account) query.set('account', params.account);
    return apiFetch<{ summary: AccountingSummary; ledger: { items: AccountingLedgerEntry[]; total: number; page: number; limit: number; pageSize: number; totalPages: number } }>(`/admin/accounting?${query.toString()}`, { signal });
  },
  // ─── SPEC-040 §3.3 Track 12 D8 — Accounting (new typed endpoints) ──────────
  // Anti-§6 (CM-19/CM-15): all return typed DTOs from @profileia/shared.
  // Write ops require superadmin + 2FA — enforced by the backend
  // requireSuperAdmin middleware (the frontend just calls the endpoint;
  // 403 means the user lacks the role).
  /** GET /admin/accounting/summary — totals + perNetwork breakdown.
   *  Sprint 18 Phase 4: response validated with Zod to catch shape drift
   *  between backend + frontend (anti-§6 CM-19).
   *  D-013 fix (SPEC-043 Batch D): added optional `signal` param.
   *  SPEC-105 Task D.3 (P-167): eliminado el cast enmascarador
   *  `as unknown as ZodType<AccountingSummary>` — era la máscara que
   *  dejaba pasar el drift DTO↔schema a nivel de tipos (tsc verde,
   *  runtime amputando). Con la aserción de paridad tipo-level
   *  (api-responses.parity.ts) + el tipado directo, cualquier drift
   *  futuro revienta en compile-time. */
  getAccountingSummary(signal?: AbortSignal): Promise<AccountingSummary> {
    return apiFetchValidated<AccountingSummary>(
      '/admin/accounting/summary',
      accountingSummaryResponseSchema,
      { signal },
    );
  },
  /** GET /admin/accounting/ledger — paginated ledger entries (?account=, ?refType=)
   *  D-013 fix (SPEC-043 Batch D): added optional `signal` param.
   *  ADMIN-TRANSPARENCY (Task ID: ADMIN-TRANSPARENCY): added optional
   *  `refType` param (Feature 1 — used by the accounting-history Sheet to
   *  scope the ledger to 'deposit' | 'payout' | 'compensation' | …). The
   *  backend validates `refType` against a whitelist enum (defense-in-depth
   *  — same pattern as the existing `account` param). */
  getLedgerEntries(params?: { page?: number; limit?: number; account?: string; refType?: string }, signal?: AbortSignal): Promise<{
    items: AccountingLedgerEntry[];
    total: number;
    page: number;
    limit: number;
    pageSize: number;
    totalPages: number;
  }> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.account) query.set('account', params.account);
    if (params?.refType) query.set('refType', params.refType);
    return apiFetch(`/admin/accounting/ledger?${query.toString()}`, { signal });
  },
  /** GET /admin/accounting/balance/:account — balance for a single account
   *  D-013 fix (SPEC-043 Batch D): added optional `signal` param. */
  getAccountBalance(account: string, signal?: AbortSignal): Promise<AccountBalanceResult> {
    return apiFetch<AccountBalanceResult>(`/admin/accounting/balance/${encodeURIComponent(account)}`, { signal });
  },
  /** GET /admin/accounting/trial-balance — trial balance + balanced flag
   *  D-013 fix (SPEC-043 Batch D): added optional `signal` param. */
  getTrialBalance(signal?: AbortSignal): Promise<TrialBalanceResult> {
    return apiFetch<TrialBalanceResult>('/admin/accounting/trial-balance', { signal });
  },
  /** GET /admin/accounting/reconciliation — latest reconciliation per network
   *  D-013 fix (SPEC-043 Batch D): added optional `signal` param. */
  getReconciliationStatus(signal?: AbortSignal): Promise<ReconciliationStatusResponse> {
    return apiFetch<ReconciliationStatusResponse>('/admin/accounting/reconciliation', { signal });
  },
  /** GET /admin/accounting/reconciliation/:network/history — history per network
   *  D-013 fix (SPEC-043 Batch D): added optional `signal` param. */
  getReconciliationHistory(network: AccountingNetwork, limit?: number, signal?: AbortSignal): Promise<ReconciliationHistoryResponse> {
    const query = new URLSearchParams();
    if (limit !== undefined) query.set('limit', String(limit));
    return apiFetch<ReconciliationHistoryResponse>(`/admin/accounting/reconciliation/${network}/history?${query.toString()}`, { signal });
  },
  /** POST /admin/accounting/reconciliation/run — trigger immediate reconciliation (superadmin) */
  runReconciliation(network?: AccountingNetwork): Promise<ReconciliationReport | ReconciliationResult> {
    const query = new URLSearchParams();
    if (network) query.set('network', network);
    return apiFetch(`/admin/accounting/reconciliation/run?${query.toString()}`, { method: 'POST' });
  },
  /** POST /admin/accounting/adjustment — post manual adjustment entry (superadmin) */
  postManualAdjustment(input: {
    account: string;
    amount: string;
    direction: 'debit' | 'credit';
    reason: string;
  }): Promise<AccountingPostResult> {
    return apiFetch<AccountingPostResult>('/admin/accounting/adjustment', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  // ADM-05: Payouts (CONTRACTS-ADMIN-API §6.1 — Paginated<PayoutDto>).
  // SPEC-040 D4 alignment: `pageSize` → `limit` (canonical per §0.3).
  // D-013 fix (SPEC-043 Batch D): added optional `signal` param.
  listPayouts(params?: { page?: number; limit?: number; status?: PayoutStatus }, signal?: AbortSignal): Promise<Paginated<PayoutDto>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);
    return apiFetch<Paginated<PayoutDto>>(`/admin/payouts?${query.toString()}`, { signal });
  },
  reviewPayout(payoutId: string, decision: 'approved' | 'rejected', reason?: string) {
    // AUDIT-5-001 fix (QA Co-founder SPEC-046): backend Zod schema REQUIRES
    // `reason` (min 1, max 1000 chars) when decision='rejected'. When
    // decision='approved', `reason` is optional and omitted.
    const body: Record<string, unknown> = { decision };
    if (decision === 'rejected' && reason !== undefined) {
      body.reason = reason;
    }
    return apiFetch<{ success: boolean }>(`/admin/payouts/${payoutId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  // Sprint 17 Track B-3 — Admin-wallet-connect semi-automatic payout
  // signing endpoints (Profileia pattern, no KMS keys at launch). The
  // admin connects MetaMask to the platform wallet address, then signs
  // each approved payout individually. See
  // `backend/src/services/admin-wallet-signer.service.ts` for the flow.
  //
  //   1. GET  /admin/payouts/awaiting-signatures  → queue
  //   2. POST /admin/payouts/:id/prepare-tx       → unsigned tx data
  //   3. (frontend) window.ethereum.request({method:'eth_sendTransaction'})
  //   4. POST /admin/payouts/:id/submit-tx        → record txHash
  //   5. GET  /admin/payouts/:id/tx-status        → poll until completed
  listAwaitingSignatures(limit?: number, signal?: AbortSignal) {
    const query = new URLSearchParams();
    if (limit !== undefined) query.set('limit', String(limit));
    // Sprint 18 Phase 4: validate response shape — the admin is about to
    // sign real on-chain transactions, so a shape mismatch here would
    // cause the MetaMask popup to send to the wrong address/amount.
    // D-013 fix (SPEC-043 Batch D): pass signal to apiFetchValidated.
    return apiFetchValidated<AwaitingSignaturePayout[]>(
      `/admin/payouts/awaiting-signatures${query.toString() ? `?${query.toString()}` : ''}`,
      awaitingSignaturesResponseSchema as unknown as ZodType<AwaitingSignaturePayout[]>,
      { signal },
    );
  },
  preparePayoutTx(payoutId: string, adminWallet: string) {
    // Sprint 18 Phase 4: validate unsigned tx shape BEFORE passing to
    // MetaMask — a malformed `to` or `value` would cause an irreversible
    // on-chain mistake.
    return apiFetchValidated<UnsignedTransaction>(
      `/admin/payouts/${payoutId}/prepare-tx`,
      prepareTxResponseSchema as unknown as ZodType<UnsignedTransaction>,
      { method: 'POST', body: JSON.stringify({ adminWallet }) },
    );
  },
  submitPayoutTx(payoutId: string, txHash: string) {
    return apiFetchValidated<SubmitTransactionResult>(
      `/admin/payouts/${payoutId}/submit-tx`,
      submitTxResponseSchema as unknown as ZodType<SubmitTransactionResult>,
      { method: 'POST', body: JSON.stringify({ txHash }) },
    );
  },
  getPayoutTxStatus(payoutId: string, signal?: AbortSignal) {
    // Sprint 18 Phase 4: validate tx status response — the polling UI
    // renders confirmations + explorer URL, so a missing field would
    // crash the React render loop.
    // D-013 fix (SPEC-043 Batch D): pass signal to apiFetchValidated.
    return apiFetchValidated<TransactionStatusResult>(
      `/admin/payouts/${payoutId}/tx-status`,
      payoutTxStatusResponseSchema as unknown as ZodType<TransactionStatusResult>,
      { signal },
    );
  },
  // SPEC-195 (Sprint 99, 2026-09-12): Unified approve-and-pay endpoint.
  // Combines PUT /payouts/:id {decision:'approved'} + POST /submit-tx
  // into ONE atomic operation. Returns the same shape as submitPayoutTx
  // (SubmitTransactionResult with status='processing').
  approveAndPayPayout(payoutId: string, txHash: string) {
    return apiFetchValidated<SubmitTransactionResult>(
      `/admin/payouts/${payoutId}/approve-and-pay`,
      submitTxResponseSchema as unknown as ZodType<SubmitTransactionResult>,
      { method: 'POST', body: JSON.stringify({ txHash }) },
    );
  },
  // ADM-06: Audit — D-013 fix (SPEC-043 Batch D): added optional `signal` param.
  // AUDIT3-SHARED-003 (SPEC-046 QA AUDIT R1 IA — BATCH-6): consume the
  // canonical `AuditListQuery` DTO from `@profileia/shared/dto/audit`.
  // Replaces the deprecated `pageSize` with `limit` (CONTRACTS §0.3) and
  // adds the previously-missing `actor`, `target`, `from`, `to` filters.
  // Return type is now `PaginatedAuditEntries` (no more `any`).
  listAudit(params?: AuditListQuery, signal?: AbortSignal): Promise<PaginatedAuditEntries> {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.action) query.set('action', params.action);
    if (params?.actor) query.set('actor', params.actor);
    if (params?.target) query.set('target', params.target);
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    return apiFetch<PaginatedAuditEntries>(`/admin/audit?${query.toString()}`, { signal });
  },
  // AUDIT3-SHARED-004 (SPEC-046 QA AUDIT R1 IA — BATCH-6): typed return —
  // `AuditChainVerificationResult` (no more `any`).
  verifyAudit(signal?: AbortSignal): Promise<AuditChainVerificationResult> {
    return apiFetch<AuditChainVerificationResult>('/admin/audit/verify', { method: 'POST', signal });
  },
  // ADM-07: Feature flags — D-013 fix (SPEC-043 Batch D): added optional `signal` param.
  // AUDIT3-SHARED-006 (SPEC-046 QA AUDIT R1 IA — BATCH-6): typed return —
  // `FeatureFlagDto[]` (no more `any`).
  listFeatures(signal?: AbortSignal): Promise<FeatureFlagDto[]> {
    return apiFetch<FeatureFlagDto[]>('/admin/features', { signal });
  },
  updateFeature(key: string, enabled: boolean, rolloutPercent?: number): Promise<FeatureFlagDto> {
    return apiFetch<FeatureFlagDto>(`/admin/features/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled, rolloutPercent }),
    });
  },
  // ADM-08: Announcements (CONTRACTS-ADMIN-API §3.1-§3.4, Track 7-backend).
  // Returns Paginated<AnnouncementDto> per §0.3 + §3.1.
  // SPEC-040 D3 — backend now returns both `limit` (canonical) and
  // `pageSize` (deprecated alias). Frontend reads `limit` directly.
  // Query: `limit` preferred; `pageSize` accepted as legacy alias.
  listAnnouncements(params?: {
    page?: number;
    limit?: number;
    pageSize?: number;  // deprecated alias for `limit`
    target?: 'global' | 'package' | 'user';
    type?: 'info' | 'warning' | 'urgent' | 'maintenance';
    active?: boolean;
    q?: string;
  }, signal?: AbortSignal): Promise<Paginated<AnnouncementDto>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    // `limit` is the canonical param (CONTRACTS §0.3). `pageSize` kept
    // as legacy alias for callers that haven't migrated yet.
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    else if (params?.pageSize) query.set('pageSize', String(params.pageSize));
    if (params?.target) query.set('target', params.target);
    if (params?.type) query.set('type', params.type);
    if (typeof params?.active === 'boolean') query.set('active', String(params.active));
    if (params?.q) query.set('q', params.q);
    // D-013 fix (SPEC-043 Batch D): pass signal to apiFetch.
    return apiFetch<Paginated<AnnouncementDto>>(`/admin/announcements?${query.toString()}`, { signal });
  },
  createAnnouncement(data: CreateAnnouncementBody): Promise<AnnouncementDto> {
    return apiFetch<AnnouncementDto>('/admin/announcements', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  // SPEC-040 D3 — `PUT` → `PATCH` to align with CONTRACTS-ADMIN-API §3.3
  // (UpdateAnnouncementBody has all fields optional — semantically a PATCH).
  // Backend keeps `PUT` as a deprecated alias for 1 sprint (Sprint 16 removal).
  updateAnnouncement(
    announcementId: string,
    data: UpdateAnnouncementBody,
  ): Promise<AnnouncementDto> {
    return apiFetch<AnnouncementDto>(`/admin/announcements/${announcementId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
  deleteAnnouncement(announcementId: string): Promise<{ success: true } | void> {
    return apiFetch<{ success: true } | void>(
      `/admin/announcements/${announcementId}`,
      { method: 'DELETE' },
    );
  },
  // ADM-09: Conferences — D-013 fix (SPEC-043 Batch D): added optional `signal` param.
  // AUDIT-5-013 fix (QA Co-founder SPEC-046): add `limit` (canonical per CONTRACTS §0.3).
  // AUDIT3-SHARED-006 (SPEC-046 QA AUDIT R1 IA — BATCH-6): typed return —
  // `Paginated<AdminConferenceDto>` (no more `any`).
  listConferences(params?: { page?: number; limit?: number; pageSize?: number; status?: string }, signal?: AbortSignal): Promise<Paginated<AdminConferenceDto>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    else if (params?.pageSize) query.set('pageSize', String(params.pageSize));
    if (params?.status) query.set('status', params.status);
    return apiFetch<Paginated<AdminConferenceDto>>(`/admin/conferences?${query.toString()}`, { signal });
  },
  // AUDIT3-SHARED-006: typed return — `AdminConferenceDto` (no more `any`).
  terminateConference(roomId: string): Promise<AdminConferenceDto> {
    return apiFetch<AdminConferenceDto>(`/admin/conferences/${roomId}`, {
      method: 'PUT',
      body: JSON.stringify({ action: 'terminate' }),
    });
  },
  // ADM-10: Metrics — D-013 fix (SPEC-043 Batch D): added optional `signal` param.
  // AUDIT3-SHARED-006 (SPEC-046 QA AUDIT R1 IA — BATCH-6): typed return —
  // `AdminMetricsDto` (no more `any`).
  getMetrics(signal?: AbortSignal): Promise<AdminMetricsDto> {
    return apiFetch<AdminMetricsDto>('/admin/metrics', { signal });
  },
  // ADM-11: Analytics (SPEC-035 §2.2 Track B) — D-013 fix (SPEC-043 Batch D): added optional `signal` param.
  getAdminGMV(signal?: AbortSignal): Promise<{ monthly: Array<{ month: string; gmv: string }> }> {
    return apiFetch('/admin/analytics/gmv', { signal });
  },
  getAdminRetention(signal?: AbortSignal): Promise<{ renewalRate: number; churnRate: number }> {
    return apiFetch('/admin/analytics/retention', { signal });
  },
  getAdminPackages(signal?: AbortSignal): Promise<Array<{ tier: string; count: number }>> {
    return apiFetch('/admin/analytics/packages', { signal });
  },
  // QA-131-006 (SPEC-QA-AUDIT-SPRINT-131 §5): deposits 30-day aggregation.
  // Returns aggregated deposits (count + USDT sum) for the last 30 days,
  // broken down by day + by network. The response shape is anchored by
  // the shared Zod schema (TRAMPA P-259 — `adminDeposits30dResponseSchema`
  // in `@profileia/shared/schemas/admin-analytics`). Anti-§6: PURE data
  // aggregation — no business logic, no status filter.
  getAdminDeposits30d(signal?: AbortSignal): Promise<{
    totalDeposits: number;
    totalAmount: string;
    byDay: Array<{ date: string; count: number; amount: string }>;
    byNetwork: Array<{ network: 'BSC' | 'Polygon' | 'Arbitrum'; count: number; amount: string }>;
  }> {
    return apiFetch('/admin/analytics/deposits/30d', { signal });
  },
  // ADM-12: Transactions (CONTRACTS-ADMIN-API §7.1 — GET /admin/transactions).
  // BUG-EXP5-002 (SPEC-041 QA audit pre-launch): previously the
  // /admin/transactions page was a skeleton with no API wiring. The
  // backend route `adminRouter.get('/transactions', ...)` was already
  // LIVE (returning an empty paginated placeholder). This method wires
  // the frontend to that endpoint with full typed query support per
  // CONTRACTS §7.1 (ListTransactionsQuery: type, status, network,
  // userId, account, page, limit, q, from, to).
  //
  // Returns Paginated<TransactionDto>. Backend may return an empty
  // list while the Track 4 unified ledger migration is incomplete —
  // the page handles empty state gracefully.
  listTransactions(params?: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
    network?: string;
    userId?: string;
    account?: string;
    q?: string;
    from?: string;
    to?: string;
  }, signal?: AbortSignal): Promise<Paginated<TransactionDto>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.type) query.set('type', params.type);
    if (params?.status) query.set('status', params.status);
    if (params?.network) query.set('network', params.network);
    if (params?.userId) query.set('userId', params.userId);
    if (params?.account) query.set('account', params.account);
    if (params?.q) query.set('q', params.q);
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    // D-013 fix (SPEC-043 Batch D): pass signal to apiFetch.
    return apiFetch<Paginated<TransactionDto>>(`/admin/transactions?${query.toString()}`, { signal });
  },
  // ADM-13: Circuit Breakers (CONTRACTS-ADMIN-API §9.1 — GET /admin/circuit-breakers).
  // BUG-EXP5-001 (SPEC-041 QA audit pre-launch): previously the
  // /admin/circuit-breakers page was a skeleton with no API wiring.
  // The backend route `adminRouter.get('/circuit-breakers', ...)`
  // was already LIVE (returns `CircuitBreakerMetricsDto[]`). This
  // method + `resetCircuitBreaker` below wire the frontend to the
  // bulkheading operational view per CONTRACTS §9 (GET + POST reset)
  // and §14.6 (WS event `circuit-breaker:state-change`).
  // M-7 fix (QA-AUDIT): added `signal?: AbortSignal` param for proper
  // request cancellation support.
  listCircuitBreakers(signal?: AbortSignal): Promise<CircuitBreakerMetricsDto[]> {
    return apiFetch<CircuitBreakerMetricsDto[]>('/admin/circuit-breakers', { signal });
  },
  // CONTRACTS-ADMIN-API §9.2 — POST /admin/circuit-breakers/:service/reset.
  // Auth: admin + `requireRecent2fa` (backend enforces; the frontend
  // surfaces 403 2FA_REQUIRED via the global event hook in apiFetch).
  // Returns CircuitBreakerResetResultDto (currentState is always CLOSED
  // after a manual reset per CONTRACTS §9.2).
  //
  // R4-INT-020 TODO (BATCH-6): the backend circuit-breaker reset stub
  // currently returns `{ success: false, ... }` even on a successful
  // reset (the response body says success=false but the breaker IS
  // actually reset). This is misleading — the admin UI shows a green
  // "Reset successful" toast but the underlying data says success:false.
  // Fix: backend should return `{ success: true }` on successful reset.
  // Tracked as backend TODO — no frontend change needed once fixed.
  resetCircuitBreaker(
    service: string,
    reason?: string,
  ): Promise<CircuitBreakerResetResultDto> {
    return apiFetch<CircuitBreakerResetResultDto>(
      `/admin/circuit-breakers/${encodeURIComponent(service)}/reset`,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      },
    );
  },
  // ─── SPEC-071 (Academia Profileia · Fase 0) — Admin academy CRUD ──────────
  //
  // Mounted under `/api/admin/academy/*` per SPEC-071 §4.10 + §4.11.
  // All routes require superadmin + recent 2FA (INV-SEC-06 §9.6 —
  // voiding a certificate is an irreversible financial-grade op).
  // The frontend calls these from `/admin/academy` (minimal CRUD page
  // for the Founder to preview/manage the 8 placeholder draft courses).
  //
  // Anti-§6 (CM-19/CM-15): pure API wrappers — no business logic. Each
  // method delegates to the backend `academy.controller.ts` handlers
  // (handleCreateCourse, handleUpdateCourse, etc.) which in turn call
  // the academy.service.ts. NO business logic in this file.
  //
  // Spec ref: SPEC-071 §4.10, §4.11, §6 (Datos Seed Fase 0).

  // ─── V126-F3-ADMIN-AUTO-WITHDRAW-DASHBOARD — 3 read-only GET endpoints ────
  //
  // Mounted under `/api/admin/auto-withdraw/*`. Auth: router-level
  // `requireAuthAdminDual` (admin role + active status, NO 2FA recency —
  // dashboard reads are NOT financial operations per SPEC-NEGOCIO §9.6).
  //
  // Anti-§6 (CM-19/CM-15): pure API wrappers — 0 business logic. Each
  // method delegates to `controllers/admin/handlers/auto-withdraw.ts` which
  // in turn calls `features/admin/auto-withdraw.ts`. NO new endpoints
  // invented — these 3 wrappers consume the 3 endpoints created by V126-F3.
  // Anti-P-347: reuses `PayoutDto` + `Paginated<T>` from `./core/shared-types`.
  // Anti-P-382: reads the `Payout.autoWithdraw` + `User.wallet.autoWithdrawEnabled`
  // flags already populated by the V126-F1 cron — NO parallel data store.
  //
  // Spec ref: docs/CONTRATO-COFOUNDER-QA.md §10.6, §10.7, V126-F3 task spec.
  getAutoWithdrawStats(signal?: AbortSignal): Promise<AutoWithdrawStats> {
    return apiFetch<AutoWithdrawStats>('/admin/auto-withdraw/stats', { signal });
  },
  listAutoWithdrawUsers(
    params?: { page?: number; limit?: number },
    signal?: AbortSignal,
  ): Promise<Paginated<AutoWithdrawUserDto>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    return apiFetch<Paginated<AutoWithdrawUserDto>>(`/admin/auto-withdraw/users?${query.toString()}`, { signal });
  },
  listAutoWithdrawHistory(
    params?: ListAutoWithdrawHistoryQuery,
    signal?: AbortSignal,
  ): Promise<Paginated<PayoutDto>> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);
    if (params?.chain) query.set('chain', params.chain);
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params?.dateTo) query.set('dateTo', params.dateTo);
    return apiFetch<Paginated<PayoutDto>>(`/admin/auto-withdraw/history?${query.toString()}`, { signal });
  },

  academy: adminAcademy,
  market: adminMarket,
};

